<template>
  <section :id="blok.id" :class="getComponentClass(blok)">
    <div class="container">
      <h2 class="job-list__title">{{ blok.title }}</h2>
      <ul v-if="jobs.length" class="job-list__list">
        <li v-for="job in jobs" :key="job.id" class="job-list__list-item">
          <nuxt-link :to="'/' + job.full_slug">
            <div class="job-list__active-mark icon icon--arrow"></div>
            <div>
              <span class="job-list__category">{{ job.content.category }}</span>
              <p class="job-list__name">{{ job.content.title }}</p>
            </div>
          </nuxt-link>
        </li>
      </ul>
      <p v-if="!jobs.length" class="job-list__not-available">
        {{ blok.noJobMessage }}
      </p>
    </div>
  </section>
</template>

<script>
export default {
  props: {
    blok: {
      type: Object,
      default: () => {},
    },
    visible: {
      type: Boolean,
      default: () => false,
    },
    visibleSectionIndex: {
      type: Number,
      default: () => 1000,
    },
  },
  data() {
    return {
      jobs: [],
    }
  },
  watch: {
    visible(visible) {
      if (visible) {
        this.$store.commit(
          'globals/setVisibleSectionName',
          this.blok.breadcrumb
        )
        this.$store.commit(
          'globals/setVisibleSectionIndex',
          this.visibleSectionIndex
        )
      }
    },
  },
  mounted() {
    this.$storyapi
      .get('cdn/stories', {
        starts_with: this.blok.api ? this.blok.api : 'join-us/',
        is_startpage: '0',
        cv: this.$store.state.cacheVersion,
      })
      .then((res) => {
        this.jobs = res.data.stories
      })
      .catch((res) => {
        // eslint-disable-next-line no-console
        console.error('Job List - Failed to load resource', res)
      })
  },
  methods: {
    getComponentClass(blok) {
      const height = blok.isHScreen ? ' h-screen' : 'h-default'
      const space = blok.space ? `space--${blok.space}` : 'space--small'
      return `job-list theme-dark ${height} ${space}`
    },
  },
}
</script>

<style lang="scss">
@import '../../assets/scss/settings';

.job-list {
  background-image: linear-gradient(
    135deg,
    $grad-gradient 0%,
    $black 50%,
    $grad-gradient 100%
  );

  &.h-default {
    min-height: 300px;
  }

  &__title {
    @include font-size(36);
    @include font-family($font-family-headings-bold, $headings-font-weight);

    margin: 0;
  }

  &__list {
    list-style: none;
    padding: 0;
    margin-bottom: 0;
  }

  &__list-item {
    display: flex;
    margin-top: 30px;

    a {
      display: flex;

      &:hover {
        .job-list__category,
        .job-list__name {
          color: $supernova;
        }

        .job-list__active-mark {
          animation-name: showArrow;
          animation-timing-function: ease-out;
          opacity: 1;
          width: 48px;
          transform: scale(1);
          margin-right: 15px;

          &::before {
            color: $mine-shaft;
          }
        }
      }
    }
  }

  &__category {
    @include font-size(14);
  }

  &__name {
    @include font-family($font-family-headings-bold, $headings-font-weight);

    margin: 4px 0 0;
  }

  &__active-mark {
    opacity: 0;
    width: 0;
    background-color: $supernova;
    border-radius: 24px;
    height: 48px;
    margin: 4px 0 0 0;
    text-align: center;
    vertical-align: middle;
    animation-duration: 0.3s;
    animation-timing-function: ease-in;
    animation-name: hideArrow;
    transform: scale(0);

    &::before {
      vertical-align: middle;
      line-height: 48px;
      color: $mine-shaft;
    }
  }

  &__not-available {
    @include font-size(20);

    margin: 0;
  }
}

@include media-breakpoint-up(md) {
  .job-list {
    &__title {
      @include font-size(83, rem-v(96));
    }

    &__list-item {
      margin-top: 70px;

      a {
        &:hover {
          .job-list__active-mark {
            animation-name: showArrowDesktop;
            width: 54px;
            margin-right: 40px;
          }
        }
      }
    }

    &__name {
      @include font-size(36);
    }

    &__active-mark {
      border-radius: 27px;
      height: 54px;
      margin: 12px 0 0 0;
      animation-name: hideArrowDesktop;

      &::before {
        line-height: 54px;
      }
    }
  }
}

@keyframes showArrow {
  0% {
    opacity: 0;
    width: 0;
    transform: scale(0);
    margin-right: 0;
  }
  50% {
    opacity: 0;
    width: 48px;
    transform: scale(0);
    margin-right: 15px;
  }
  100% {
    opacity: 1;
    width: 48px;
    transform: scale(1);
    margin-right: 15px;
  }
}

@keyframes hideArrow {
  0% {
    opacity: 1;
    width: 48px;
    transform: scale(1);
    margin-right: 15px;
  }
  50% {
    opacity: 0;
    width: 48px;
    transform: scale(0);
    margin-right: 15px;
  }
  100% {
    opacity: 0;
    width: 0;
    transform: scale(0);
    margin-right: 0;
  }
}

@keyframes showArrowDesktop {
  0% {
    opacity: 0;
    width: 0;
    transform: scale(0);
    margin-right: 0;
  }
  50% {
    opacity: 0;
    width: 54px;
    transform: scale(0);
    margin-right: 40px;
  }
  100% {
    opacity: 1;
    width: 54px;
    transform: scale(1);
    margin-right: 40px;
  }
}

@keyframes hideArrowDesktop {
  0% {
    opacity: 1;
    width: 54px;
    transform: scale(1);
    margin-right: 40px;
  }
  50% {
    opacity: 0;
    width: 54px;
    transform: scale(0);
    margin-right: 40px;
  }
  100% {
    opacity: 0;
    width: 0;
    transform: scale(0);
    margin-right: 0;
  }
}
</style>
